import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
// Icons ke liye (Aap FontAwesome ya Lucide use kar sakte hain)
import { CheckCircle, Package, Truck, Home, MapPin, ShoppingBag, Calendar, DollarSign, User } from "lucide-react"; 

function TrackOrder() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchOrder();
  }, [orderId]);

  const fetchOrder = async () => {
    try {
      console.log("Fetching order for ID:", orderId);
      console.log("Full API URL:", `https://uniformnx-com.onrender.com/orders/single/${orderId}`);
      
      setLoading(true);
      setError(null);
      
      const res = await axios.get(`https://uniformnx-com.onrender.com/orders/single/${orderId}`);
      console.log("Full response:", res);
      console.log("Order data received:", res.data);
      
      if (!res.data) {
        throw new Error("No order data received");
      }
      
      setOrder(res.data);
    } catch (err) {
      console.error("Full error object:", err);
      console.error("Error response:", err.response);
      console.error("Error status:", err.response?.status);
      console.error("Error data:", err.response?.data);
      
      let errorMessage = "Failed to fetch order details";
      
      if (err.response?.status === 404) {
        errorMessage = "Order not found";
      } else if (err.response?.status === 500) {
        errorMessage = "Server error. Please try again later";
      } else if (err.code === "NETWORK_ERROR") {
        errorMessage = "Network error. Check your connection";
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    { label: "Order Placed", status: "Pending", icon: <Package size={20} /> },
    { label: "Picked up", status: "Picked", icon: <MapPin size={20} /> },
    { label: "On The Way", status: "Out For Delivery", icon: <Truck size={20} /> },
    { label: "Delivered", status: "Delivered", icon: <Home size={20} /> },
  ];

  // Logic to find current active index
  const getActiveIndex = () => {
    if (!order) return 0;
    return steps.findIndex(s => s.status === order.status);
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div style={{ fontSize: '16px', color: '#64748b' }}>Loading order details...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div style={{ fontSize: '16px', color: '#ef4444', marginBottom: '20px' }}>❌ {error}</div>
            <button 
              style={{ ...styles.btn, width: 'auto', padding: '10px 20px' }} 
              onClick={fetchOrder}
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!order) return <div className="loading">Updating Status...</div>;

  const activeIndex = getActiveIndex();

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerContent}>
            <div style={styles.orderInfo}>
              <h2 style={styles.title}>Track Your Order</h2>
              <p style={styles.orderId}>
                Order ID: <span style={styles.orderIdSpan}>#{order._id.slice(-8).toUpperCase()}</span>
              </p>
            </div>
            <div style={styles.statusBadge}>
              <span style={styles.statusText}>{order.status}</span>
            </div>
          </div>
        </div>

        {/* Progress Stepper */}
        <div style={styles.progressSection}>
          <h3 style={styles.sectionTitle}>Delivery Progress</h3>
          <div style={styles.stepperContainer}>
            {steps.map((step, i) => (
              <div key={i} style={styles.stepWrapper}>
                {i !== 0 && (
                  <div style={{
                    ...styles.line,
                    background: i <= activeIndex ? "#22c55e" : "#e2e8f0"
                  }}></div>
                )}
                <div style={{
                  ...styles.iconCircle,
                  background: i <= activeIndex ? "#22c55e" : "white",
                  border: i <= activeIndex ? "2px solid #22c55e" : "2px solid #e2e8f0",
                  color: i <= activeIndex ? "white" : "#94a3b8",
                }}>
                  {i < activeIndex ? <CheckCircle size={20} /> : step.icon}
                </div>
                <div style={{
                  ...styles.label,
                  color: i <= activeIndex ? "#1e293b" : "#94a3b8",
                  fontWeight: i <= activeIndex ? "600" : "400"
                }}>
                  {step.label}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Order Details */}
        <div style={styles.detailsSection}>
          <h3 style={styles.sectionTitle}>Order Details</h3>
          <div style={styles.detailsGrid}>
            <div style={styles.detailCard}>
              <div style={styles.detailIcon}>
                <Calendar size={20} color="#097969" />
              </div>
              <div style={styles.detailContent}>
                <div style={styles.detailLabel}>Order Date</div>
                <div style={styles.detailValue}>{new Date(order.createdAt).toLocaleDateString()}</div>
              </div>
            </div>
            <div style={styles.detailCard}>
              <div style={styles.detailIcon}>
                <DollarSign size={20} color="#097969" />
              </div>
              <div style={styles.detailContent}>
                <div style={styles.detailLabel}>Total Amount</div>
                <div style={styles.detailValue}>₹{order.totalAmount}</div>
              </div>
            </div>
            <div style={styles.detailCard}>
              <div style={styles.detailIcon}>
                <User size={20} color="#097969" />
              </div>
              <div style={styles.detailContent}>
                <div style={styles.detailLabel}>Customer</div>
                <div style={styles.detailValue}>{order.address?.name || "N/A"}</div>
              </div>
            </div>
            <div style={styles.detailCard}>
              <div style={styles.detailIcon}>
                <Package size={20} color="#097969" />
              </div>
              <div style={styles.detailContent}>
                <div style={styles.detailLabel}>Payment</div>
                <div style={styles.detailValue}>{order.paymentMethod || "COD"}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Uniform Items */}
        <div style={styles.uniformSection}>
          <h3 style={styles.sectionTitle}>
            <ShoppingBag size={20} style={{ marginRight: '8px' }} />
            Uniform Items
          </h3>
          <div style={styles.uniformGrid}>
            {order.items?.map((item, idx) => (
              <div key={idx} style={styles.uniformCard}>
                <div style={styles.uniformImage}>
                  <img
                    src={
                      item.uniformId?.image
                        ? `https://uniformnx-com.onrender.com/uploads/${item.uniformId.image}`
                        : "https://via.placeholder.com/80"
                    }
                    alt={item.name}
                    style={styles.uniformImg}
                  />
                </div>
                <div style={styles.uniformDetails}>
                  <h4 style={styles.uniformName}>{item.name || "Uniform Item"}</h4>
                  <div style={styles.uniformMeta}>
                    <span style={styles.uniformPrice}>₹{item.price}</span>
                    <span style={styles.uniformQty}>Qty: {item.quantity}</span>
                  </div>
                  <div style={styles.uniformTotal}>
                    Total: <strong>₹{(item.price * item.quantity).toFixed(2)}</strong>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Delivery Address */}
        <div style={styles.addressSection}>
          <h3 style={styles.sectionTitle}>
            <MapPin size={20} style={{ marginRight: '8px' }} />
            Delivery Address
          </h3>
          <div style={styles.addressCard}>
            <div style={styles.addressContent}>
              <div style={styles.addressName}>{order.address?.name}</div>
              <div style={styles.addressLine}>{order.address?.addressLine}</div>
              <div style={styles.addressCity}>
                {order.address?.city}, {order.address?.pincode}
              </div>
              <div style={styles.addressPhone}>📱 {order.address?.phone}</div>
            </div>
          </div>
        </div>

        {/* Refresh Button */}
        <div style={styles.buttonSection}>
          <button style={styles.refreshBtn} onClick={() => window.location.reload()}>
            <Truck size={16} style={{ marginRight: '8px' }} />
            Refresh Status
          </button>
        </div>
      </div>
    </div>
  );
}

// Modern Styles for enhanced UI
const styles = {
  container: {
    padding: "60px 20px",
    background: "white",
    minHeight: "100vh",
    fontFamily: "'Inter', sans-serif"
  },
  card: {
    maxWidth: "800px",
    margin: "0 auto",
    background: "white",
    padding: "40px",
    borderRadius: "20px",
    boxShadow: "0 20px 40px rgba(0,0,0,0.1)"
  },
  header: {
    marginBottom: "40px",
  },
  headerContent: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px"
  },
  orderInfo: {
    textAlign: "left"
  },
  title: {
    margin: "0 0 8px 0",
    color: "#1e293b",
    fontSize: "28px",
    fontWeight: "800"
  },
  orderId: {
    margin: "0",
    color: "#64748b",
    fontSize: "14px"
  },
  orderIdSpan: {
    color: "#097969",
    fontWeight: "700"
  },
  statusBadge: {
    background: "linear-gradient(135deg, #097969, #0d9263)",
    padding: "8px 16px",
    borderRadius: "20px",
    boxShadow: "0 4px 12px rgba(9, 121, 105, 0.3)"
  },
  statusText: {
    color: "white",
    fontWeight: "600",
    fontSize: "12px",
    textTransform: "uppercase"
  },
  sectionTitle: {
    margin: "0 0 20px 0",
    color: "#1e293b",
    fontSize: "18px",
    fontWeight: "700",
    display: "flex",
    alignItems: "center"
  },
  progressSection: {
    marginBottom: "40px",
    padding: "30px",
    background: "#f8fafc",
    borderRadius: "16px",
    border: "1px solid #e2e8f0"
  },
  stepperContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    position: "relative"
  },
  stepWrapper: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    flex: 1,
    position: "relative"
  },
  iconCircle: {
    width: "50px",
    height: "50px",
    borderRadius: "50%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    transition: "all 0.4s ease",
    zIndex: 2,
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)"
  },
  line: {
    position: "absolute",
    height: "3px",
    width: "100%",
    top: "25px",
    right: "50%",
    zIndex: 1,
    transition: "all 0.4s ease"
  },
  label: {
    marginTop: "12px",
    fontSize: "12px",
    textAlign: "center",
    fontWeight: "600"
  },
  detailsSection: {
    marginBottom: "40px"
  },
  detailsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "16px"
  },
  detailCard: {
    display: "flex",
    alignItems: "center",
    padding: "16px",
    background: "#f8fafc",
    borderRadius: "12px",
    border: "1px solid #e2e8f0",
    transition: "all 0.3s ease"
  },
  detailIcon: {
    width: "40px",
    height: "40px",
    borderRadius: "10px",
    background: "rgba(9, 121, 105, 0.1)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginRight: "12px"
  },
  detailContent: {
    flex: 1
  },
  detailLabel: {
    fontSize: "12px",
    color: "#64748b",
    marginBottom: "4px"
  },
  detailValue: {
    fontSize: "16px",
    fontWeight: "700",
    color: "#1e293b"
  },
  uniformSection: {
    marginBottom: "40px"
  },
  uniformGrid: {
    display: "grid",
    gap: "16px"
  },
  uniformCard: {
    display: "flex",
    padding: "20px",
    background: "#f8fafc",
    borderRadius: "12px",
    border: "1px solid #e2e8f0",
    transition: "all 0.3s ease"
  },
  uniformImage: {
    width: "80px",
    height: "80px",
    borderRadius: "10px",
    overflow: "hidden",
    marginRight: "16px",
    flexShrink: 0
  },
  uniformImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover"
  },
  uniformDetails: {
    flex: 1
  },
  uniformName: {
    margin: "0 0 8px 0",
    fontSize: "16px",
    fontWeight: "700",
    color: "#1e293b"
  },
  uniformMeta: {
    display: "flex",
    gap: "16px",
    marginBottom: "8px"
  },
  uniformPrice: {
    color: "#097969",
    fontWeight: "600"
  },
  uniformQty: {
    color: "#64748b",
    fontSize: "14px"
  },
  uniformTotal: {
    fontSize: "14px",
    color: "#64748b"
  },
  addressSection: {
    marginBottom: "40px"
  },
  addressCard: {
    padding: "20px",
    background: "#f8fafc",
    borderRadius: "12px",
    border: "1px solid #e2e8f0"
  },
  addressContent: {
    lineHeight: "1.6"
  },
  addressName: {
    fontSize: "16px",
    fontWeight: "700",
    color: "#1e293b",
    marginBottom: "8px"
  },
  addressLine: {
    color: "#64748b",
    marginBottom: "4px"
  },
  addressCity: {
    color: "#64748b",
    marginBottom: "4px"
  },
  addressPhone: {
    color: "#097969",
    fontWeight: "600"
  },
  buttonSection: {
    textAlign: "center"
  },
  refreshBtn: {
    padding: "14px 28px",
    background: "linear-gradient(135deg, #097969, #0d9263)",
    color: "white",
    border: "none",
    borderRadius: "12px",
    fontWeight: "700",
    fontSize: "14px",
    cursor: "pointer",
    transition: "all 0.3s ease",
    boxShadow: "0 4px 12px rgba(9, 121, 105, 0.3)",
    display: "inline-flex",
    alignItems: "center"
  },
  btn: {
    width: "100%",
    padding: "12px",
    border: "none",
    borderRadius: "8px",
    background: "#1e293b",
    color: "white",
    fontWeight: "600",
    cursor: "pointer",
    transition: "0.3s"
  }
};

export default TrackOrder;